import React, { useState } from "react";
import {
  Popover,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Loading from "components/common/loading/Loading";
import { csnValidationSchema } from "schemas/csnSchema";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { showErrorToast } from "utils/toast";

function CsnModal(props) {
  const { anchorEl, dataRow, close, update } = props;
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    close();
  };

  const open = Boolean(anchorEl);
  const actualAnchorEl = anchorEl?.element || anchorEl;

  const handleSubmit = async (values) => {
    const { csn } = values;
    setIsLoading(true);

    try {
      const jobId = dataRow._id;
      const newData = {
        csn,
        status: "pending",
        failed_reason: [],
      };

      if (csn !== dataRow.csn) {
        await update(jobId, newData);
      }
      close();
    } catch (error) {
      showErrorToast("Error updating CSN");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={actualAnchorEl}
      anchorPosition={anchorEl?.position}
      anchorReference={anchorEl?.position ? "anchorPosition" : "anchorEl"}
      onClose={isLoading ? undefined : handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      disableEnforceFocus={true}
      sx={{ zIndex: 1500 }}
      disableRestoreFocus={true}
    >
      <Box sx={{ width: 350 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
          }}
        >
          <Typography variant="h6">
            {dataRow.csn?.trim() || dataRow.order_number?.trim()
              ? "Edit CSN/Order ID"
              : "Add CSN/Order ID"}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {isLoading ? (
          <Box sx={{ p: 3 }}>
            <Loading />
          </Box>
        ) : (
          <>
            {dataRow?.firstName && dataRow?.lastName && (
              <Typography
                sx={{ px: 2.5, pt: 1, fontSize: "12px", fontWeight: 500 }}
              >
                <span style={{ color: "#757575" }}>PATIENT NAME: </span>
                <span style={{ color: "#212121" }}>
                  {`${dataRow.firstName} ${dataRow.lastName}`}
                </span>
              </Typography>
            )}

            <Formik
              initialValues={{ csn: dataRow.csn || dataRow.order_number || "" }}
              validationSchema={csnValidationSchema}
              onSubmit={(values) => handleSubmit(values)}
            >
              {({ handleSubmit, touched, errors, values }) => (
                <Form onSubmit={handleSubmit}>
                  <Box sx={{ px: 2.5, py: 2.5 }}>
                    <Field
                      name="csn"
                      as={TextField}
                      label="CSN/Order ID"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      helperText={
                        touched.csn ? <ErrorMessage name="csn" /> : ""
                      }
                      error={touched.csn && !!errors.csn}
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        width: "100%",
                        marginTop: "16px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClose}
                        fullWidth
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={!values.csn?.trim()}
                        fullWidth
                        sx={{
                          backgroundColor: "#94BA33",
                          color: "#ffffff",
                          "&:hover": {
                            backgroundColor: "#94BA33",
                          },
                        }}
                      >
                        {dataRow.csn?.trim() || dataRow.order_number?.trim()
                          ? "Edit & Resend"
                          : "Add & Resend"}
                      </Button>
                    </div>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Box>
    </Popover>
  );
}

export default CsnModal;
